import React, { useState, useMemo } from 'react';

const COLORS = {
  primary: '#FD7E14', // Orange
  white: '#FFFFFF',
  black: '#000000',
  gray: '#D3D3D3', // Light Grey
  darkGray: '#A9A9A9',
};

const StateListScreen = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [imageViewVisible, setImageViewVisible] = useState(false);

  const [newStateName, setNewStateName] = useState('');
  const [mapFile, setMapFile] = useState(null);
  const [editingState, setEditingState] = useState(null);
  const [selectedImageUri, setSelectedImageUri] = useState(null);

  const [activeFilterType, setActiveFilterType] = useState('name');
  const [filterValue, setFilterValue] = useState('');
  const [loading, setLoading] = useState(false);

  const [states, setStates] = useState([
    {
      id: 1,
      name: 'California',
      map: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=400&h=250&fit=crop',
      districts: 58,
      population: '39.5M',
      lastUpdated: '2024-01-15',
      area: '423,970 km²',
      capital: 'Sacramento',
    },
    {
      id: 2,
      name: 'Texas',
      map: 'https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?w=400&h=250&fit=crop',
      districts: 254,
      population: '29.1M',
      lastUpdated: '2024-01-10',
      area: '695,662 km²',
      capital: 'Austin',
    },
    {
      id: 3,
      name: 'New York',
      map: 'https://images.unsplash.com/photo-1500916434205-0c77489c6cf7?w=400&h=250&fit=crop',
      districts: 62,
      population: '19.8M',
      lastUpdated: '2024-01-12',
      area: '141,297 km²',
      capital: 'Albany',
    },
    {
      id: 4,
      name: 'Florida',
      map: 'https://images.unsplash.com/photo-1520263115673-610416f52ab6?w=400&h=250&fit=crop',
      districts: 67,
      population: '21.5M',
      lastUpdated: '2024-01-08',
      area: '170,312 km²',
      capital: 'Tallahassee',
    },
    {
      id: 5,
      name: 'Colorado',
      map: 'https://images.unsplash.com/photo-1464822759844-d2d1bae9eb0f?w=400&h=250&fit=crop',
      districts: 64,
      population: '5.8M',
      lastUpdated: '2024-01-05',
      area: '269,601 km²',
      capital: 'Denver',
    },
    {
      id: 6,
      name: 'Washington',
      map: 'https://images.unsplash.com/photo-1579033462043-0f11a7862f7d?w=400&h=250&fit=crop',
      districts: 39,
      population: '7.7M',
      lastUpdated: '2024-01-03',
      area: '184,661 km²',
      capital: 'Olympia',
    },
  ]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: COLORS.white,
      padding: '24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: COLORS.black,
    },
    headerSection: {
      maxWidth: '1200px',
      margin: '0 auto 32px',
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '32px',
    },
    titleSection: {
      color: COLORS.black,
    },
    mainTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      margin: '0 0 8px 0',
      color: COLORS.primary,
    },
    subtitle: {
      fontSize: '1.1rem',
      color: COLORS.darkGray,
      margin: 0,
    },
    addButton: {
      backgroundColor: COLORS.primary,
      border: 'none',
      color: COLORS.white,
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '16px',
      transition: 'background-color 0.3s ease',
      borderRadius: 12,
      borderColor: COLORS.primary,
      borderStyle: 'solid',
    },
    statsSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      maxWidth: '1200px',
      margin: '0 auto 32px',
    },
    statsCard: {
      backgroundColor: COLORS.gray,
      borderRadius: '16px',
      padding: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      color: COLORS.black,
      boxShadow: `0 2px 8px rgba(0,0,0,0.1)`,
      userSelect: 'none',
    },
    statsIcon: { fontSize: '2rem' },
    statsContent: { flex: 1 },
    statsValue: {
      fontSize: '1.8rem',
      fontWeight: '700',
      marginBottom: '4px',
    },
    statsLabel: {
      fontSize: '0.9rem',
      opacity: 0.7,
    },
    controlsSection: {
      maxWidth: '1200px',
      margin: '0 auto 32px',
    },
    searchContainer: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
    },
    searchWrapper: {
      position: 'relative',
      flex: 1,
      maxWidth: '400px',
    },
    searchInput: {
      width: '100%',
      padding: '16px 20px 16px 48px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '12px',
      backgroundColor: COLORS.white,
      fontSize: '1rem',
      boxShadow: 'none',
      color: COLORS.black,
    },
    searchIcon: {
      position: 'absolute',
      left: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: COLORS.darkGray,
    },
    filterButton: {
      backgroundColor: COLORS.primary,
      border: 'none',
      padding: '12px 20px',
      borderRadius: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: COLORS.white,
      transition: 'background-color 0.3s ease',
    },
    statesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
      gap: '24px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    stateCard: {
      backgroundColor: COLORS.white,
      borderRadius: '20px',
      padding: '24px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease',
      border: `1px solid ${COLORS.border}`,
      color: COLORS.black,
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    },
    stateBadge: {
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '600',
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
    },
    actionButton: {
      background: COLORS.white,
      border: `1px solid ${COLORS.border}`,
      padding: '6px 10px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '1.1rem',
      transition: 'background-color 0.2s ease',
      color: COLORS.primary,
    },
    stateName: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: COLORS.black,
      marginBottom: '16px',
    },
    mapPreview: {
      position: 'relative',
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '16px',
      cursor: 'pointer',
      transition: 'transform 0.3s ease',
      border: `1px solid ${COLORS.border}`,
    },
    mapImage: {
      width: '100%',
      height: '200px',
      objectFit: 'cover',
      display: 'block',
      borderRadius: '12px',
    },
    mapOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      color: COLORS.white,
      fontWeight: '600',
      borderRadius: '12px',
      pointerEvents: 'none',
    },
    stateDetails: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      color: COLORS.darkGray,
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    detailLabel: {
      fontSize: '0.8rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      color: COLORS.darkGray,
    },
    detailValue: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: COLORS.black,
    },
    modalBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modal: {
      backgroundColor: COLORS.white,
      borderRadius: '20px',
      padding: '32px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      color: COLORS.black,
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '2rem',
      cursor: 'pointer',
      color: COLORS.darkGray,
      padding: 0,
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      transition: 'all 0.2s ease',
    },
    modalBody: {
      marginBottom: '24px',
    },
    formGroup: {
      marginBottom: '20px',
    },
    formLabel: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: COLORS.black,
    },
    formInput: {
      width: '100%',
      padding: '14px 16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '12px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      color: COLORS.black,
    },
    fileUploadArea: {
      border: `2px dashed ${COLORS.gray}`,
      borderRadius: '12px',
      padding: '20px',
      textAlign: 'center',
      cursor: 'pointer',
      color: COLORS.darkGray,
      userSelect: 'none',
    },
    fileUploadContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    fileIcon: { fontSize: '1.2rem' },
    modalActions: {
      display: 'flex',
      gap: '12px',
    },
    primaryButton: {
      flex: 1,
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      border: 'none',
      padding: '14px 20px',
      borderRadius: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'background-color 0.3s ease',
    },
    secondaryButton: {
      flex: 1,
      backgroundColor: COLORS.white,
      color: COLORS.black,
      border: `1px solid ${COLORS.border}`,
      padding: '14px 20px',
      borderRadius: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'border-color 0.3s ease',
    },
    filterTypeGroup: {
      display: 'flex',
      gap: '8px',
    },
    filterTypeButton: {
      flex: 1,
      padding: '12px 16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      backgroundColor: COLORS.white,
      color: COLORS.black,
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    filterTypeButtonActive: {
      flex: 1,
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      fontWeight: '600',
      cursor: 'pointer',
      border: `1px solid ${COLORS.primary}`,
      transition: 'all 0.3s ease',
    },
    emptyState: {
      textAlign: 'center',
      padding: '80px 20px',
      color: COLORS.darkGray,
    },
    emptyIcon: { fontSize: '4rem', marginBottom: '16px' },
    emptyTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '8px',
    },
    emptyDescription: {
      fontSize: '1rem',
      opacity: 0.8,
      marginBottom: '24px',
    },
    emptyActionButton: {
      backgroundColor: COLORS.primary,
      border: 'none',
      color: COLORS.white,
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontSize: '1rem',
    },
    imageModalContent: {
      width: '90%',
      height: '90%',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    fullSizeImage: {
      maxWidth: '95%',
      maxHeight: '95%',
      objectFit: 'contain',
    },
  };

  // Helpers and handlers

  const filteredStates = useMemo(() => {
    let data = Array.isArray(states) ? states : [];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(
        s =>
          s.name.toLowerCase().includes(q) ||
          s.id.toString().includes(q) ||
          s.capital.toLowerCase().includes(q)
      );
    }
    if (filterValue) {
      const fv = filterValue.toLowerCase().trim();
      data = data.filter(s => {
        const v = { id: s.id.toString(), name: s.name }[activeFilterType] || '';
        return v.toLowerCase().includes(fv);
      });
    }
    return data.sort((a, b) => b.id - a.id);
  }, [searchQuery, states, activeFilterType, filterValue]);

  const clearAddModal = () => {
    setNewStateName('');
    setMapFile(null);
    setAddModalVisible(false);
  };

  const handleFileSelect = () => {
    setMapFile({ name: 'state_map.png' });
  };

  const handleSave = () => {
    if (!newStateName.trim()) {
      alert('State name is required');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const newState = {
        id: states.length + 1,
        name: newStateName,
        map: 'https://images.unsplash.com/photo-1464822759844-d2d1bae9eb0f?w=400&h=250&fit=crop',
        districts: Math.floor(Math.random() * 100) + 1,
        population: `${(Math.random() * 50).toFixed(1)}M`,
        lastUpdated: new Date().toISOString().split('T')[0],
        area: `${(Math.random() * 500000).toFixed(0)} km²`,
        capital: 'Capital City',
      };
      setStates([newState, ...states]);
      setLoading(false);
      clearAddModal();
    }, 1000);
  };

  const handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      setStates(states.filter((state) => state.id !== item.id));
    }
  };

  const handleEdit = (item) => {
    setEditingState({ ...item });
    setMapFile(null);
    setEditModalVisible(true);
  };

  const handleUpdate = () => {
    if (!editingState || !editingState.name.trim()) {
      alert('State name is required');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setStates(states.map((item) => (item.id === editingState.id ? editingState : item)));
      setLoading(false);
      setEditModalVisible(false);
      setEditingState(null);
    }, 1000);
  };

  const handleViewMap = (item) => {
    if (item.map) {
      setSelectedImageUri(item.map);
      setImageViewVisible(true);
    } else {
      alert('No map available for this state');
    }
  };

  const handleClearFilter = () => {
    setFilterValue('');
    setFilterModalVisible(false);
  };

  const StatsCard = ({ icon, value, label }) => (
    <div style={styles.statsCard}>
      <div style={styles.statsIcon}>{icon}</div>
      <div style={styles.statsContent}>
        <div style={styles.statsValue}>{value}</div>
        <div style={styles.statsLabel}>{label}</div>
      </div>
    </div>
  );

  const FilterTypeButton = ({ type, active, onClick }) => (
    <button
      style={active ? styles.filterTypeButtonActive : styles.filterTypeButton}
      onClick={onClick}
      type="button"
    >
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </button>
  );

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.mainTitle}>State Management</h1>
            <p style={styles.subtitle}>Manage states and their geographical data</p>
          </div>
          <button style={styles.addButton} onClick={() => setAddModalVisible(true)} type="button">
            <span>+</span> Add New State
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div style={styles.statsSection}>
        <StatsCard icon="🏛️" value={states.length} label="Total States" />
        <StatsCard icon="🗺️" value={states.filter((s) => s.map).length} label="With Maps" />
        <StatsCard icon="📊" value={states.reduce((acc, s) => acc + s.districts, 0)} label="Total Districts" />
        <StatsCard
          icon="👥"
          value={states.reduce((acc, s) => acc + parseFloat(s.population), 0).toFixed(1) + 'M'}
          label="Total Population"
        />
      </div>

      {/* Controls Section */}
      <div style={styles.controlsSection}>
        <div style={styles.searchContainer}>
          <div style={styles.searchWrapper}>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="Search states..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span style={styles.searchIcon}>🔍</span>
          </div>
          <button style={styles.filterButton} onClick={() => setFilterModalVisible(true)} type="button">
            ⏷ Filters
          </button>
        </div>
      </div>

      {/* States Grid */}
      <div style={styles.statesGrid}>
        {filteredStates.map((state) => (
          <div key={state.id} style={styles.stateCard}>
            <div style={styles.cardHeader}>
              <div style={styles.stateBadge}>#{state.id}</div>
              <div style={styles.cardActions}>
                <button style={styles.actionButton} onClick={() => handleViewMap(state)} title="View Map" type="button">
                  👁️
                </button>
                <button style={styles.actionButton} onClick={() => handleEdit(state)} title="Edit" type="button">
                  ✏️
                </button>
                <button style={styles.actionButton} onClick={() => handleDelete(state)} title="Delete" type="button">
                  🗑️
                </button>
              </div>
            </div>
            <div style={styles.stateName}>{state.name}</div>
            <div
              style={styles.mapPreview}
              onClick={() => handleViewMap(state)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleViewMap(state)}
              aria-label={`View map of ${state.name}`}
            >
              <img src={state.map} alt={`${state.name} map`} style={styles.mapImage} />
              <div style={styles.mapOverlay}>
                <span>View Map</span>
              </div>
            </div>
            <div style={styles.stateDetails}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Districts</span>
                <span style={styles.detailValue}>{state.districts}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Population</span>
                <span style={styles.detailValue}>{state.population}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Area</span>
                <span style={styles.detailValue}>{state.area}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Capital</span>
                <span style={styles.detailValue}>{state.capital}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredStates.length === 0 && !loading && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🗺️</div>
          <h3 style={styles.emptyTitle}>No states found</h3>
          <p style={styles.emptyDescription}>
            {searchQuery || filterValue
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by adding your first state'}
          </p>
          <button style={styles.emptyActionButton} onClick={() => setAddModalVisible(true)} type="button">
            Add State
          </button>
        </div>
      )}

      {/* Image View Modal */}
      {imageViewVisible && (
        <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
          <div style={styles.imageModalContent}>
            <button
              style={styles.closeButton}
              onClick={() => setImageViewVisible(false)}
              aria-label="Close map view"
              type="button"
            >
              ×
            </button>
            {selectedImageUri && <img src={selectedImageUri} alt="State Map" style={styles.fullSizeImage} />}
          </div>
        </div>
      )}

      {/* Add State Modal */}
      {addModalVisible && (
        <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add New State</h2>
              <button
                style={styles.closeButton}
                onClick={clearAddModal}
                aria-label="Close add state modal"
                type="button"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>State Name</label>
                <input
                  type="text"
                  style={styles.formInput}
                  placeholder="Enter state name"
                  value={newStateName}
                  onChange={(e) => setNewStateName(e.target.value)}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Map File</label>
                <div
                  style={styles.fileUploadArea}
                  onClick={handleFileSelect}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleFileSelect()}
                  aria-label="Select map file"
                >
                  <div style={styles.fileUploadContent}>
                    <span style={styles.fileIcon}>📎</span>
                    <span style={{ flex: 1 }}>{mapFile ? mapFile.name : 'Click to select map file'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button style={styles.secondaryButton} onClick={clearAddModal} type="button">
                Cancel
              </button>
              <button style={styles.primaryButton} onClick={handleSave} disabled={loading} type="button">
                {loading ? 'Adding...' : 'Add State'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit State Modal */}
      {editModalVisible && editingState && (
        <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit State</h2>
              <button
                style={styles.closeButton}
                onClick={() => setEditModalVisible(false)}
                aria-label="Close edit state modal"
                type="button"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>State Name</label>
                <input
                  type="text"
                  style={styles.formInput}
                  placeholder="Enter state name"
                  value={editingState.name}
                  onChange={(e) => setEditingState({ ...editingState, name: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Map File</label>
                <div
                  style={styles.fileUploadArea}
                  onClick={handleFileSelect}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleFileSelect()}
                  aria-label="Select new map file"
                >
                  <div style={styles.fileUploadContent}>
                    <span style={styles.fileIcon}>📎</span>
                    <span style={{ flex: 1 }}>{mapFile ? mapFile.name : 'Select new map file (optional)'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button style={styles.secondaryButton} onClick={() => setEditModalVisible(false)} type="button">
                Cancel
              </button>
              <button style={styles.primaryButton} onClick={handleUpdate} disabled={loading} type="button">
                {loading ? 'Updating...' : 'Update State'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {filterModalVisible && (
        <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Filter States</h2>
              <button
                style={styles.closeButton}
                onClick={() => setFilterModalVisible(false)}
                aria-label="Close filter modal"
                type="button"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Filter By</label>
                <div style={styles.filterTypeGroup}>
                  <FilterTypeButton type="name" active={activeFilterType === 'name'} onClick={() => setActiveFilterType('name')} />
                  <FilterTypeButton type="id" active={activeFilterType === 'id'} onClick={() => setActiveFilterType('id')} />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Filter Value</label>
                <input
                  type={activeFilterType === 'id' ? 'number' : 'text'}
                  style={styles.formInput}
                  placeholder={`Enter ${activeFilterType} to filter...`}
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button style={styles.secondaryButton} onClick={handleClearFilter} type="button">
                Clear Filters
              </button>
              <button style={styles.primaryButton} onClick={() => setFilterModalVisible(false)} type="button">
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateListScreen;
